import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, CheckPicker, Checkbox, Loader, Message } from 'rsuite';
import { ConfirmModal, FilterField, FilterToolbar, renderSelectAllFooter } from '@nsi-advisory-services/components';

import PageHeader from '../components/shared/PageHeader';
import PayPeriodSelect from '../components/shared/PayPeriodSelect';
import PayrollMatrix from '../components/payroll/PayrollMatrix';
import PayrollSettingsModal from '../components/payroll/PayrollSettingsModal';
import PayrollRunInfoModal from '../components/payroll/PayrollRunInfoModal';
import PayrollReimbursementDriftModal from '../components/payroll/PayrollReimbursementDriftModal';
import PayrollCompleteRunModal from '../components/payroll/PayrollCompleteRunModal';
import PayrollRepullModal from '../components/payroll/PayrollRepullModal';
import {
  getCurrentPayPeriod,
  getPayPeriodOptions,
  toDateOnlyString,
} from '../utils/payPeriods';
import {
  usePayrollEmployees,
  useSavePayrollEmployerMatch,
  useSavePayrollCorrection,
  usePayrollTimesheet,
  usePayrollRuns,
  useGetPayrollRun,
  useSavePayrollRun,
  useUpdatePayrollRun,
  useDeletePayrollRun,
  useSavePayrollRunCorrection,
  useSavePayrollRetirementAdjustment,
  useSavePayrollRunRetirementAdjustment,
  useSavePayrollRunReimbursementAdjustment,
  useSavePayrollRunReimbursementAdjustmentBulk,
  usePayrollRunReimbursementDrift,
  useRefreshPayrollRunReimbursements,
  useCompletePayrollRun,
  useSetPayrollRunJournalEntry,
  usePayrollRunExports,
  useRecordPayrollRunExport,
  useSetPayrollRunSharePoint,
  useRepullPayrollRun,
  usePayrollRunTimesheetDrift,
} from '../hooks/useQueries';
import { showToast } from '../utils/toast';
import './PayrollPage.css';
import { renderEmployeeSelection } from '../components/shared/employeeSelection';

// Payroll is a company-specific module. See client/src/config/payrollFeature.js
// for the switch that hides it and server/config/payrollConfig.js for the
// classification rules; nothing outside those touches it.

const periodValue = ({ start, end }) => `${toDateOnlyString(start)}|${toDateOnlyString(end)}`;

const splitPeriodValue = (value) => {
  const [start, end] = String(value || '').split('|');
  return start && end ? { start, end } : null;
};

// "Aug 16 - Aug 31, 2026". The year rides on the end date only when both halves
// share one, which they always do for a semi-monthly period.
const formatRange = (start, end) => {
  const s = start ? new Date(`${start}T00:00:00`) : null;
  const e = end ? new Date(`${end}T00:00:00`) : null;
  if (!s || !e || Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return `${start || '?'} - ${end || '?'}`;
  const sameYear = s.getFullYear() === e.getFullYear();
  const sTxt = s.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) });
  const eTxt = e.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${sTxt} - ${eTxt}`;
};

// Saved runs, gathered into the month they belong to.
//
// Keyed on the period's START date. A semi-monthly period never straddles a
// month boundary, so start and end agree - but if one ever did, the month the
// work began in is the one the run would be filed under.
//
// The list arrives ordered by period descending, so walking it in order gives
// months newest-first and the runs inside each already in the right order. No
// second sort, and nothing to keep in step with the query's ORDER BY.
const groupRunsByMonth = (runs) => {
  const months = [];
  const byKey = new Map();

  for (const run of runs) {
    const start = run.period_start ? new Date(`${run.period_start}T00:00:00`) : null;
    if (!start || Number.isNaN(start.getTime())) continue;

    const key = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}`;
    let month = byKey.get(key);
    if (!month) {
      month = {
        key,
        label: start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        runs: [],
      };
      byKey.set(key, month);
      months.push(month);
    }
    month.runs.push(run);
  }

  return months;
};

const fmtMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '\u2014';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
};

const fmtHours = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0.00';
  return n.toFixed(2);
};

// Each category gets one accent, used on the group band and the tile foot so a
// column of numbers can be read back to a category without the label.
const CATEGORY_ACCENT = {
  direct: 'var(--bdc-primary)',
  indirect: '#8a6d3b',
  fringe: '#5a4b8a',
};

const CATEGORY_ICON = {
  direct: 'bi-briefcase',
  indirect: 'bi-diagram-3',
  fringe: 'bi-heart-pulse',
};

// One employee's breakdown: a framed table whose head names them, with a
// labelled band opening each category and a subtotal closing it.
//
// Closed by default. The head already carries the three category totals, so a
// closed row is not a row with nothing in it - it is the summary, and opening it
// is for when you want to see which tasks made up those totals. With a full
// roster on screen that is the difference between a page you can scan and one
// you have to scroll for a minute.
function EmployeeBreakdown({ employee }) {
  const worked = employee.totalHours > 0;
  const [open, setOpen] = useState(false);
  const bodyId = useId();

  return (
    <div className="nsi-table-wrap payroll-employee">
      {/* The whole head is the target rather than the chevron alone - the
          chevron is a hint about state, not a hit area. A <span> carries the
          title because a heading cannot live inside a button. */}
      <button
        type="button"
        className="bdc-table-head payroll-employee__head"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-controls={bodyId}
      >
        <div className="bdc-table-head__heading">
          <i className="bi bi-chevron-down payroll-employee__chevron" aria-hidden="true" />
          <span className="bdc-table-head__title">{employee.name}</span>
          {employee.email ? <span className="bdc-table-head__note">{employee.email}</span> : null}
        </div>
        <div className="bdc-table-head__actions payroll-employee__totals">
          {/* Only where there is one. The Total beside it already includes the
              correction, so without this the chips do not add up to it and the
              hours look wrong rather than adjusted. */}
          {employee.correction ? (
            <span className="payroll-chip payroll-chip--correction">
              <span className="payroll-chip__label">Correction</span>
              <span className="payroll-chip__value bdc-num">
                {employee.correction > 0 ? '+' : ''}{fmtHours(employee.correction)}
              </span>
            </span>
          ) : null}

          {employee.categories.map((category) => (
            <span
              key={category.key}
              className="payroll-chip"
              style={{ '--payroll-chip-accent': CATEGORY_ACCENT[category.key] }}
            >
              <span className="payroll-chip__label">{category.label}</span>
              <span className="payroll-chip__value bdc-num">{fmtHours(category.hours)}</span>
            </span>
          ))}
          <span className="payroll-chip payroll-chip--total">
            <span className="payroll-chip__label">Total</span>
            <span className="payroll-chip__value bdc-num">{fmtHours(employee.totalHours)}</span>
          </span>
        </div>
      </button>

      {/* Stays mounted so it has a height to animate to; `inert` keeps a closed
          breakdown out of the tab order and off the accessibility tree. */}
      <div id={bodyId} className="payroll-employee__body" data-open={open ? 'true' : 'false'}>
        <div className="payroll-employee__bodyinner" inert={open ? undefined : ''}>
          {worked ? (
            <table className="nsi-table">
              <thead>
                <tr>
                  <th>Task</th>
                  <th>Task ID</th>
                  <th className="nsi-table__right">Entries</th>
                  <th className="nsi-table__right">Hours</th>
                </tr>
              </thead>
              <tbody>
                {employee.categories.map((category) => {
                  // A category the person logged nothing to would otherwise print a
                  // band and a zero, three times over on a part-period. Say it once
                  // in the chips above and leave the table to the real work.
                  if (category.tasks.length === 0) return null;

                  return (
                    <React.Fragment key={category.key}>
                      <tr
                        className="payroll-group"
                        style={{ '--payroll-group-accent': CATEGORY_ACCENT[category.key] }}
                      >
                        <td colSpan={4}>
                          <span className="payroll-group__label">
                            <i className={`bi ${CATEGORY_ICON[category.key]}`} aria-hidden="true" />
                            {category.label}
                          </span>
                          <span className="payroll-group__meta">
                            {category.taskCount} task{category.taskCount === 1 ? '' : 's'}
                          </span>
                        </td>
                      </tr>

                      {category.tasks.map((task) => (
                        <tr key={`${category.key}:${task.taskId}`}>
                          <td className="bdc-table-wrap">
                            <div className="payroll-task__name">{task.taskName || task.label}</div>
                            {task.breadcrumb && task.breadcrumb !== task.taskName ? (
                              <div className="payroll-task__path">{task.breadcrumb}</div>
                            ) : null}
                            {!task.resolvedFromCatalog ? (
                              <div className="payroll-task__warn">
                                <i className="bi bi-exclamation-triangle" aria-hidden="true" /> Not in the TimeCamp task
                                catalog - categorised from the entry&rsquo;s own billable flag.
                              </div>
                            ) : null}
                          </td>
                          <td className="bdc-num nsi-table__muted">{task.taskId}</td>
                          <td className="nsi-table__right bdc-num nsi-table__muted">{task.entryCount}</td>
                          <td className="nsi-table__right bdc-num">{fmtHours(task.hours)}</td>
                        </tr>
                      ))}

                      <tr className="payroll-subtotal">
                        <td colSpan={3}>{category.label} total</td>
                        <td className="nsi-table__right bdc-num">{fmtHours(category.hours)}</td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2}>Total</td>
                  <td className="nsi-table__right bdc-num">{employee.entryCount}</td>
                  <td className="nsi-table__right bdc-num">{fmtHours(employee.totalHours)}</td>
                </tr>
              </tfoot>
            </table>
          ) : (
            // A selected employee with nothing logged is the finding, not an
            // omission - dropping them is how somebody gets missed on a pay run.
            <div className="nsi-table__empty">No time logged in this pay period.</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PayrollPage() {
  const payPeriodOptions = useMemo(() => getPayPeriodOptions(), []);

  const currentPeriodValue = useMemo(() => periodValue(getCurrentPayPeriod()), []);

  // Open on the last *completed* period rather than the one in progress: that
  // is the one being paid. Options are newest first, so the entry after the
  // current one is the previous period.
  const defaultPeriodValue = useMemo(() => {
    const currentIndex = payPeriodOptions.findIndex((option) => option.value === currentPeriodValue);
    return payPeriodOptions[currentIndex + 1]?.value || currentPeriodValue;
  }, [payPeriodOptions, currentPeriodValue]);

  const [period, setPeriod] = useState(defaultPeriodValue);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [result, setResult] = useState(null);

  const [settingsOpen, setSettingsOpen] = useState(false);

  // 'home' lists the saved runs; 'run' is the sheet. `savedRun` is the stored
  // row when an old run is open, and null while a new one is being built - it
  // is what makes the sheet read-only, because a saved run is a record of what
  // was paid and nothing on it may move afterwards.
  const [view, setView] = useState('home');
  const [savedRun, setSavedRun] = useState(null);
  const [openingRun, setOpeningRun] = useState(false);
  const [driftOpen, setDriftOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);

  // Which run is open lives in the query string, not only in state.
  //
  // The page used to move between the run list and a run entirely in memory,
  // so the browser saw one location the whole time and Back left the payroll
  // section altogether. Putting it in the URL makes Back mean "the run list",
  // makes Forward work, and makes a run's address something that can be
  // bookmarked or pasted to somebody.
  //
  //   (nothing)   the run list
  //   ?run=new    a run being built, not saved yet
  //   ?run=<id>   a saved run
  //
  // The URL is the source of truth: the handlers below write to it and an
  // effect reacts, rather than each handler setting state and the URL
  // separately and the two drifting apart on Back.
  const [searchParams, setSearchParams] = useSearchParams();
  const runParam = searchParams.get('run');

  // Written with replace when the new-run screen becomes a saved run: there is
  // no useful history entry for "the same run, a moment before it was filed".
  const setRunParam = useCallback((next, { replace = false } = {}) => {
    setSearchParams((current) => {
      const params = new URLSearchParams(current);
      if (next) params.set('run', next);
      else params.delete('run');
      return params;
    }, { replace });
  }, [setSearchParams]);
  const [runToDelete, setRunToDelete] = useState(null);
  const [infoOpen, setInfoOpen] = useState(false);

  const employeesQuery = usePayrollEmployees();
  const saveMatch = useSavePayrollEmployerMatch();
  const saveCorrection = useSavePayrollCorrection();
  const timesheet = usePayrollTimesheet();
  const runsQuery = usePayrollRuns();
  const getRun = useGetPayrollRun();
  const saveRun = useSavePayrollRun();
  const deleteRun = useDeletePayrollRun();
  const updateRun = useUpdatePayrollRun();
  const saveRunCorrection = useSavePayrollRunCorrection();
  const saveAdjustment = useSavePayrollRetirementAdjustment();
  const saveRunAdjustment = useSavePayrollRunRetirementAdjustment();
  const saveRunReimbursementAdjustment = useSavePayrollRunReimbursementAdjustment();
  const saveRunReimbursementAdjustmentBulk = useSavePayrollRunReimbursementAdjustmentBulk();

  const isSaved = !!savedRun;

  const employeeOptions = useMemo(
    () => (employeesQuery.data || []).map((employee) => ({
      label: employee.name,
      value: employee.id,
    })),
    [employeesQuery.data]
  );

  // Every active employee starts ticked. Seeded once the roster lands, and only
  // while nothing is selected, so a later refetch cannot wipe out a choice the
  // user has already made.
  useEffect(() => {
    if (employeeOptions.length === 0) return;
    setSelectedEmployees((current) => (current.length === 0 ? employeeOptions.map((o) => o.value) : current));
  }, [employeeOptions]);

  const allSelected = employeeOptions.length > 0 && selectedEmployees.length === employeeOptions.length;

  // The match is one company-wide setting, so this is a single write. The sheet
  // is then re-pulled, because every match figure on it was derived from the
  // old rate and is now stale.
  const saveEmployerMatch = async (percent) => {
    try {
      await saveMatch.mutateAsync(percent);
    } catch (error) {
      showToast('error', error?.message || 'Could not save the employer match.');
      return;
    }

    setSettingsOpen(false);
    showToast('success', `Employer match set to ${percent}%.`);

    // Every match figure on screen was derived from the old rate and is now
    // stale. Said rather than fixed: re-pulling here would overwrite a run
    // somebody has already been through, off the back of a settings change.
    // On a saved run the button in the header is how that happens.
    if (result) {
      showToast(
        'info',
        isSaved
          ? 'The match figures on this run are now out of date. Re-pull hours to bring them up to date.'
          : 'Pull the period again so the match figures match.'
      );
    }
  };

  // Corrections feed Total Compensation, and through it the retirement figures.
  //
  // On a saved run the server owns that recalculation: it patches the stored
  // snapshot and hands it back, so the record and the screen cannot disagree.
  //
  // On a run that has not been saved there is no snapshot to patch, so the
  // figures are re-derived here instead - a TimeCamp round trip per typed
  // adjustment would make the row unusable. applyCorrection mirrors the
  // server's arithmetic and is tested against it.
  const persistCorrection = async ({ employeeId, amount, note }) => {
    if (isSaved) {
      try {
        const { result: updated, figures_updated_at: version } = await saveRunCorrection.mutateAsync({
          runId: savedRun.id, employeeId, amount, note,
        });
        setResult(updated);
        // Tracked with the figures, not alongside them: an export stamps the
        // version of whatever is on screen, so the two have to move together.
        setSavedRun((prev) => ({ ...prev, figures_updated_at: version }));
      } catch (error) {
        showToast('error', error?.message || 'Could not save the correction.');
        throw error;
      }
      return;
    }

    const range = splitPeriodValue(period);
    if (!range) return;

    try {
      await saveCorrection.mutateAsync({ employeeId, amount, note, start: range.start, end: range.end });
    } catch (error) {
      showToast('error', error?.message || 'Could not save the correction.');
      throw error;
    }

    // A correction is hours now, so it moves total hours, indirect costs, the
    // journal and the variance - too much to mirror here. The period is
    // re-pulled instead, and the server stays the only place the rules live.
    runPull();
  };

  // A completed run is the record of what was paid and takes no more edits.
  // Every affordance below is gated on this, and so is the server - see
  // refuseIfCompleted in routes/payroll.js. The buttons going is a courtesy;
  // the endpoints refusing is the lock.
  const isCompleted = Boolean(savedRun?.completed_at);

  const completeRun = useCompletePayrollRun();

  // Re-pulling a saved run. Always behind the confirmation dialog: the whole
  // point is that a pull which replaces reviewed figures is something somebody
  // chooses, not something that happens to them.
  const repull = useRepullPayrollRun();
  const [repullOpen, setRepullOpen] = useState(false);

  // Asked when the review dialog opens, because that is the last moment the
  // figures can still be corrected. Gated on the dialog being open so the
  // page does not re-pull the period in the background on every visit.
  const timesheetDrift = usePayrollRunTimesheetDrift(savedRun?.id, {
    enabled: completeOpen && Boolean(savedRun?.id) && !isCompleted,
  });

  // Takes no arguments on purpose: it is wired straight to onClick handlers,
  // which would otherwise hand it a React event as its options object.
  const repullRun = async () => {
    if (!savedRun?.id || isCompleted) return;
    try {
      const {
        result: updated, repulled_at: repulledAt, figures_updated_at: version, logs,
      } = await repull.mutateAsync(savedRun.id);
      setResult(updated);
      setSavedRun((prev) => ({
        ...prev, repulled_at: repulledAt, figures_updated_at: version,
      }));
      // Both dialogs close, whichever one started this. The figures have just
      // been replaced, so a review left open would be a review of numbers that
      // are no longer on screen - and the export taken against the old ones is
      // now stale, which is a re-read rather than a detail to carry on past.
      setRepullOpen(false);
      setCompleteOpen(false);
      showToast(
        'success',
        `Re-pulled ${updated.entryCount} entries for ${updated.employees.length} employee${updated.employees.length === 1 ? '' : 's'}.`
      );
      if (logs) console.log('[payroll repull]', logs);
    } catch (error) {
      // The stored run is untouched on a failure - the server rolls back - so
      // the screen is left showing what is still on file rather than nothing.
      showToast('error', error?.message || 'Could not re-pull this run.');
    }
  };

  // The export trail. A run cannot be completed without at least one entry -
  // the server enforces it, the dialog explains it.
  const recordExport = useRecordPayrollRunExport();
  const runExports = usePayrollRunExports(savedRun?.id);
  const setSharePoint = useSetPayrollRunSharePoint();

  const handleSharePoint = async (confirmed) => {
    if (!savedRun?.id) return;
    try {
      const saved = await setSharePoint.mutateAsync({ id: savedRun.id, confirmed });
      setSavedRun((prev) => ({ ...prev, sharepoint_confirmed_at: saved.sharepoint_confirmed_at }));
    } catch (error) {
      showToast('error', error?.message || 'Could not save that.');
    }
  };

  const handleCompleteRun = async (paymentDate, journalEntryCreated) => {
    if (!savedRun?.id) return;
    try {
      const saved = await completeRun.mutateAsync({ id: savedRun.id, paymentDate, journalEntryCreated });
      setSavedRun((prev) => ({
        ...prev,
        completed_at: saved.completed_at,
        payment_date: saved.payment_date,
        journal_entry_created: saved.journal_entry_created,
      }));
      setCompleteOpen(false);
      showToast('success', 'Payroll run completed.');
    } catch (error) {
      showToast('error', error?.message || 'Could not complete the run.');
    }
  };

  // Whether the QuickBooks journal entry has been keyed in. The one thing on a
  // saved run that is not a figure, so the only thing completing does not
  // freeze - the entry is normally created after the run is signed off.
  const setJournalEntry = useSetPayrollRunJournalEntry();
  const journalEntryCreated = Boolean(savedRun?.journal_entry_created);

  // Mirrors journalEntryCreated: a note about work done outside this system,
  // recorded on the run. Read here so both the review dialog and the completed
  // run render from the same value.
  const sharePointConfirmed = Boolean(savedRun?.sharepoint_confirmed_at);
  const sharePointUrl = runExports.data?.sharePointUrl || null;

  const handleJournalEntryChange = async (created) => {
    if (!savedRun?.id) return;
    // Moved on screen first: the box is a note somebody is ticking off a list,
    // and a checkbox that waits for a round trip before it moves feels broken.
    // Put back if the write fails.
    setSavedRun((prev) => ({ ...prev, journal_entry_created: created }));
    try {
      await setJournalEntry.mutateAsync({ id: savedRun.id, created });
    } catch (error) {
      setSavedRun((prev) => ({ ...prev, journal_entry_created: !created }));
      showToast('error', error?.message || 'Could not save the journal entry status.');
    }
  };

  // Only a saved run can drift: an unsaved one was pulled live a moment ago, and
  // re-pulling it is a button away. The query re-asks whenever the tab comes
  // back into focus, which is what makes approving an expense in another tab
  // show up here without anybody pressing anything - see the hook.
  //
  // Switched off once the run is completed: a warning about expenses that can no
  // longer be synced onto it is noise, not information.
  const driftQuery = usePayrollRunReimbursementDrift(savedRun?.id, { enabled: isSaved && !isCompleted });
  const refreshReimbursements = useRefreshPayrollRunReimbursements();

  const syncReimbursements = async () => {
    if (!savedRun?.id) return;
    try {
      const { result: updated, figures_updated_at: version } = await refreshReimbursements.mutateAsync(savedRun.id);
      setResult(updated);
      setSavedRun((prev) => ({ ...prev, figures_updated_at: version }));
      setDriftOpen(false);
      showToast('success', 'Reimbursements updated from live expenses.');
    } catch (error) {
      showToast('error', error?.message || 'Could not sync reimbursements.');
    }
  };

  // The retirement adjustment moves one figure and the Empower total, nothing
  // else - so on a saved run the patched snapshot comes straight back, and
  // before a run is filed the period is simply re-pulled.
  const persistAdjustment = async (employee, amount) => {
    if (isSaved) {
      try {
        const { result: updated, figures_updated_at: version } = await saveRunAdjustment.mutateAsync({
          runId: savedRun.id, employeeId: employee.employeeId, amount,
        });
        setResult(updated);
        setSavedRun((prev) => ({ ...prev, figures_updated_at: version }));
      } catch (error) {
        showToast('error', error?.message || 'Could not save the loan repayment.');
      }
      return;
    }

    const range = splitPeriodValue(period);
    if (!range) return;
    try {
      await saveAdjustment.mutateAsync({
        employeeId: employee.employeeId, amount, start: range.start, end: range.end,
      });
      runPull();
    } catch (error) {
      showToast('error', error?.message || 'Could not save the loan repayment.');
    }
  };

  // The Reimbursement Adjustment row: money owed that did not come through an
  // expense, typed per employee with a reason, like a correction. Saved runs
  // only - a run is filed the moment it is pulled, so that is every run being
  // worked on. Only reimbursements and Cash Needed move, so the patched
  // snapshot comes straight back.
  const persistReimbursementAdjustment = async ({ employeeId, amount, note }) => {
    if (!savedRun?.id) return;
    try {
      const { result: updated, figures_updated_at: version } = await saveRunReimbursementAdjustment.mutateAsync({
        runId: savedRun.id, employeeId, amount, note,
      });
      setResult(updated);
      setSavedRun((prev) => ({ ...prev, figures_updated_at: version }));
    } catch (error) {
      showToast('error', error?.message || 'Could not save the reimbursement adjustment.');
      throw error;
    }
  };

  // The same, for everybody picked in the row's group dialog at once.
  const persistReimbursementAdjustmentBulk = async ({ employeeIds, amount, note }) => {
    if (!savedRun?.id) return;
    try {
      const { result: updated, figures_updated_at: version } = await saveRunReimbursementAdjustmentBulk.mutateAsync({
        runId: savedRun.id, employeeIds, amount, note,
      });
      setResult(updated);
      setSavedRun((prev) => ({ ...prev, figures_updated_at: version }));
      showToast(
        'success',
        amount === 0
          ? `Reimbursement adjustment cleared for ${employeeIds.length} employee${employeeIds.length === 1 ? '' : 's'}.`
          : `Reimbursement adjustment applied to ${employeeIds.length} employee${employeeIds.length === 1 ? '' : 's'}.`
      );
    } catch (error) {
      showToast('error', error?.message || 'Could not apply the reimbursement adjustment.');
      throw error;
    }
  };

  // Loaded on demand: xlsx is a large dependency and most visits never export.
  //
  // The download is recorded against the saved run, which is what lets the run
  // be completed - see the review dialog. Recorded after the file is written,
  // so a workbook that failed to build is not logged as a copy that exists.
  //
  // A run that has not been saved yet exports fine and records nothing: there
  // is no run to record it against, and completing is not reachable from there
  // either.
  // What the workbook's Summary records under "Included".
  //
  // On a saved run this has to come from the run, not from the picker. The
  // picker is hidden once a run is filed and opening one from the list never
  // restores it, so it still holds the seeded default - every active employee.
  // A run filed for three people would have exported as "All active
  // employees", and that line is audit metadata now.
  //
  // An empty saved selection means the run was filed for whoever was active,
  // which is what it still means on a re-pull - so the wording is the same.
  const exportEmployeesLabel = () => {
    const countLabel = (n) => `${n} selected employee${n === 1 ? '' : 's'}`;

    // Only an actual array answers the question. A missing employee_ids means
    // this row does not know its selection, which is not the same as knowing it
    // was everybody - reading absence as an empty list is what labelled an
    // explicitly selected run "All active employees".
    if (savedRun && Array.isArray(savedRun.employee_ids)) {
      const ids = savedRun.employee_ids;
      return ids.length === 0 ? 'All active employees' : countLabel(ids.length);
    }

    // Either a run that was never filed, or a saved row without the field. The
    // picker still holds what produced the figures on screen in both cases.
    return allSelected ? 'All active employees' : countLabel(selectedEmployees.length);
  };

  const exportExcel = async () => {
    if (!result) return;
    try {
      // Read before the workbook is built, so it describes the figures going
      // into the file rather than whatever the run has become by the time the
      // download is reported. That gap is real: building and uploading the
      // file takes long enough for somebody else's correction to land, and a
      // version taken afterwards would mark a stale workbook as current.
      const figuresVersion = savedRun?.figures_updated_at || null;

      const mod = await import('../utils/payrollExport');
      const filename = mod.exportPayrollExcel(result, {
        employeesLabel: exportEmployeesLabel(),
        generatedAt: new Date().toLocaleString(),
      });

      if (savedRun?.id && filename) {
        // The file is already on disk at this point. A failure to log it must
        // not read as a failed download, so it is reported as what it is: the
        // copy was taken, the trail did not get it, and completing will still
        // ask for one.
        try {
          await recordExport.mutateAsync({ id: savedRun.id, filename, figuresVersion });
        } catch (error) {
          showToast('error', error?.message || 'The file downloaded, but recording it against the run failed.');
        }
      }
      return filename;
    } catch (error) {
      showToast('error', error?.message || 'Could not build the export.');
      return null;
    }
  };

  // The run in the shape of QuickBooks' Payroll details report, for upload to
  // QBO. Only the retirement columns have figures behind them; the toast says
  // what was left blank, so nobody uploads it believing it is complete.
  const exportQuickBooks = async (format) => {
    if (!result) return;
    try {
      const mod = await import('../utils/qboPayrollDetailsExport');
      mod.exportQboPayrollDetails(result, { paymentDate: savedRun?.payment_date || null, format });
      showToast(
        'warning',
        `${mod.QBO_COLUMNS_WITHOUT_DATA.length} of the report's columns are left blank - taxes, net pay, `
          + 'benefit deductions and total payroll cost are not tracked here. Fill them in before uploading.',
        { header: 'QuickBooks file downloaded', duration: 10000 }
      );
    } catch (error) {
      showToast('error', error?.message || 'Could not build the QuickBooks file.');
    }
  };

  const startNewRun = () => {
    setSavedRun(null);
    setResult(null);
    setPeriod(defaultPeriodValue);
    setView('run');
    setRunParam('new');
  };

  const backToHome = () => {
    setSavedRun(null);
    setResult(null);
    setView('home');
    setRunParam(null);
  };

  // The address changed - by a click, by Back or Forward, or by someone
  // arriving on a link - and the page follows it.
  //
  // Everything here is a no-op when state already matches, which is what makes
  // it safe to run on every change: a click sets the param, this sees the page
  // is already right, and nothing happens twice.
  useEffect(() => {
    // Back to the list. Deliberately drops the run rather than keeping it
    // behind the list, so returning to it re-reads from the server instead of
    // showing figures that may have moved on.
    if (!runParam) {
      if (view !== 'home') {
        setSavedRun(null);
        setResult(null);
        setView('home');
      }
      return;
    }

    // A run being built. Nothing to fetch; only reachable by Forward back onto
    // it, since startNewRun has already set the state up.
    if (runParam === 'new') {
      if (view !== 'run') setView('run');
      return;
    }

    // A saved run. Already open is the common case - this fires on the click
    // that navigated here - so check before fetching.
    if (savedRun?.id === runParam) return;
    if (openingRun) return;
    loadRun(runParam);
    // view is read, not tracked: adding it would re-run this on every internal
    // view change and fight the handlers for control.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runParam, savedRun?.id]);

  // Opening a saved run renders the stored snapshot. Nothing is recalculated -
  // the figures are whatever they were when the run was saved, whatever the
  // rules or the employee records say today.
  // Fetch a run and put it on screen. Separate from navigation, because the
  // Back button gets here without anybody clicking a row.
  const loadRun = useCallback(async (id) => {
    setOpeningRun(true);
    try {
      const row = await getRun(id);
      setSavedRun(row);
      setResult(row.result);
      setPeriod(`${row.period_start}|${row.period_end}`);
      setView('run');
    } catch (error) {
      showToast('error', error?.message || 'Could not open that run.');
      // Do not strand the page on a run it could not load - a deleted id in a
      // stale bookmark should land on the list, not on an empty run screen.
      setRunParam(null, { replace: true });
      setView('home');
    } finally {
      setOpeningRun(false);
    }
  }, [getRun, setRunParam]);

  // Clicking a row only changes the address. The effect below notices and
  // loads it, so a click and a Back land in exactly the same place.
  const openRun = (run) => setRunParam(run.id);

  // A run is saved as soon as its hours are pulled. Nothing is asked for up
  // front - no name, no confirmation - because the figures are what the pull is
  // for, and a form in front of them would be in the way. Naming and notes come
  // afterwards, from the run itself.
  const autoSaveRun = async (data) => {
    // The selection is settled once here and used for both the write and the
    // local row, so the two cannot describe different runs.
    const employeeIds = allSelected ? [] : selectedEmployees;

    try {
      const { id, figures_updated_at: version } = await saveRun.mutateAsync({ employeeIds, result: data });
      // Stands in for the row that was just written, so it has to carry the
      // same fields anything reads off a saved run - employee_ids included.
      // Leaving it out made the run look like it had no selection at all, and
      // an empty selection is not "unknown", it means every active employee.
      setSavedRun({
        id,
        name: null,
        notes: null,
        period_start: data.period.start,
        period_end: data.period.end,
        employee_ids: employeeIds,
        // The version these figures are of. Without it an export is stamped
        // null, and a null version never satisfies the completion guard - so
        // the first download of a freshly pulled run was reported as stale the
        // moment it finished.
        figures_updated_at: version || null,
        created_at: new Date().toISOString(),
        journal_entry_created: false,
      });
      // The new-run screen has become this run. Replaced rather than pushed:
      // Back should reach the run list, not a "new run" screen whose figures
      // are now filed under an id.
      setRunParam(id, { replace: true });
    } catch (error) {
      // The figures are on screen either way; this only means they are not
      // filed yet, which is worth saying plainly rather than swallowing.
      showToast('error', `Pulled, but could not save the run: ${error?.message || 'unknown error'}`);
    }
  };

  const saveRunInfo = async ({ name, notes }) => {
    if (!savedRun) return;
    try {
      const saved = await updateRun.mutateAsync({ id: savedRun.id, name, notes });
      setSavedRun((prev) => ({ ...prev, name: saved.name, notes: saved.notes }));
      setInfoOpen(false);
      showToast('success', 'Run information saved.');
    } catch (error) {
      showToast('error', error?.message || 'Could not save the run information.');
    }
  };

  const deleteOpenRun = async () => {
    if (!savedRun) return;
    try {
      await deleteRun.mutateAsync(savedRun.id);
      setInfoOpen(false);
      showToast('success', 'Payroll run deleted. The period can be run again.');
      backToHome();
    } catch (error) {
      showToast('error', error?.message || 'Could not delete the run.');
    }
  };

  const confirmDeleteRun = async () => {
    const target = runToDelete;
    if (!target) return;
    try {
      await deleteRun.mutateAsync(target.id);
      setRunToDelete(null);
      showToast('success', 'Payroll run deleted. The period can be run again.');
      if (savedRun?.id === target.id) backToHome();
    } catch (error) {
      showToast('error', error?.message || 'Could not delete the run.');
    }
  };

  // Pull the period for a run that has not been filed yet.
  //
  // Refuses outright once the run is saved, and that guard is the whole reason
  // this reads the way it does. A saved run's figures have been reviewed, and
  // several things on this page used to call this as a side effect of
  // something else - changing the employer match, saving a correction - which
  // meant a pull could overwrite a reviewed run without anybody asking for
  // one. A saved run is re-pulled through repullRun(), which puts a
  // confirmation in front of it.
  const runPull = () => {
    if (savedRun) return;

    const range = splitPeriodValue(period);
    if (!range) {
      showToast('error', 'Pick a pay period first.');
      return;
    }
    if (selectedEmployees.length === 0) {
      showToast('error', 'Pick at least one employee.');
      return;
    }

    timesheet.mutate(
      {
        start: range.start,
        end: range.end,
        // Sending every id and sending none mean the same thing to the server;
        // send none when it is everybody so the run is not pinned to a roster
        // that may have changed between the picker loading and the pull.
        employeeIds: allSelected ? [] : selectedEmployees,
      },
      {
        onSuccess: (data) => {
          setResult(data);
          showToast(
            'success',
            `Pulled ${data.entryCount} entries for ${data.employees.length} employee${data.employees.length === 1 ? '' : 's'}.`
          );
          // A period that already has a run cannot take another, and a re-pull
          // of one we just filed must not try to file it twice - the server
          // tells us which case this is.
          if (!data.savedRunId && !savedRun) autoSaveRun(data);
        },
        onError: (error) => {
          setResult(null);
          showToast('error', error?.message || 'The payroll pull failed.');
        },
      }
    );
  };

  const range = splitPeriodValue(period);
  const totals = result?.totals;

  // Memoised because the `|| []` fallback is a new array each render, and the
  // grouping below hangs off it.
  const runs = useMemo(() => runsQuery.data?.runs || [], [runsQuery.data?.runs]);

  // Periods that already have a run, in the picker's own "start|end" form.
  //
  // One run per period is a database constraint, so picking one of these could
  // only ever end in the pull succeeding and the save being refused - the
  // figures on screen with a notice saying they cannot be filed. Showing it as
  // taken up front is the same answer, before the work instead of after it.
  //
  // Completed or not makes no difference here: both occupy the period. What to
  // do about it differs - open the run, or delete it and pull again - and that
  // belongs on the run list, which is where the disabled row sends people.
  const runPeriodValues = useMemo(
    () => runs.map((run) => `${run.period_start}|${run.period_end}`),
    [runs]
  );

  // The picker disables these rows, but the page opens on the period that just
  // ended - the one most likely to have been run already - so the selected
  // value can be a taken period without anybody having picked it.
  const selectedPeriodRun = useMemo(
    () => runs.find((run) => `${run.period_start}|${run.period_end}` === period) || null,
    [runs, period]
  );
  const runMonths = useMemo(() => groupRunsByMonth(runs), [runs]);

  // Which month sections are folded away.
  //
  // Tracked as the closed ones rather than the open ones so a month that
  // appears later - the first run of a new month - is open without anybody
  // having to be told about it. Seeded once, when the runs first arrive, with
  // everything but the newest month closed: the run somebody came here for is
  // almost always the most recent, and a year of history unfolded above it is
  // the reason this page needed sections in the first place.
  const [closedMonths, setClosedMonths] = useState(null);

  useEffect(() => {
    if (closedMonths !== null || runMonths.length === 0) return;
    setClosedMonths(new Set(runMonths.slice(1).map((month) => month.key)));
  }, [runMonths, closedMonths]);

  const toggleMonth = (key) => {
    setClosedMonths((prev) => {
      const next = new Set(prev || []);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // ------------------------------------------------------------------ home
  const homeView = (
    <>
      <PageHeader
        crumbs={[{ label: 'Home', to: '/' }, { label: 'Admin', to: '/admin' }, { label: 'Payroll' }]}
        icon="bi-cash-stack"
        title="Payroll"
        count={runs.length || null}
        actions={(
          <Button appearance="primary" onClick={startNewRun}>
            <i className="bi bi-plus-lg me-2" aria-hidden="true" />
            New payroll run
          </Button>
        )}
      />

      {runsQuery.isError ? (
        <Message type="error" showIcon header="Could not load saved runs">
          {runsQuery.error?.message || 'Try again in a moment.'}
        </Message>
      ) : null}

      {runsQuery.isLoading || openingRun ? (
        <div className="payroll-loading">
          <Loader size="md" content={openingRun ? 'Opening run\u2026' : 'Loading saved runs\u2026'} vertical />
        </div>
      ) : runs.length === 0 && !runsQuery.isError ? (
        <div className="payroll-intro">
          <i className="bi bi-cash-stack" aria-hidden="true" />
          <h2>No saved runs yet</h2>
          <p>
            A payroll run pulls a pay period from TimeCamp and stores the finished figures, so it stays exactly as it
            was paid however the rates or the rules change afterwards.
          </p>
        </div>
      ) : (
        <div className="payroll-months">
          {runMonths.map((month) => {
            const closed = !!closedMonths?.has(month.key);
            const completed = month.runs.filter((run) => run.completed_at).length;

            return (
              <section className="payroll-month" key={month.key}>
                {/* The whole bar is the hit area; the chevron only reports the
                    state. Same shape as the per-employee breakdown on the run
                    sheet, so the two collapse the same way. */}
                <button
                  type="button"
                  className="payroll-month__head"
                  onClick={() => toggleMonth(month.key)}
                  aria-expanded={!closed}
                  aria-controls={`payroll-month-${month.key}`}
                >
                  <i className="bi bi-chevron-down payroll-month__chevron" aria-hidden="true" />
                  <span className="payroll-month__label">{month.label}</span>
                  {/* Two runs is a month kept up with, so the count is worth
                      saying on a bar somebody may never open. */}
                  <span className="payroll-month__count">
                    {month.runs.length} run{month.runs.length === 1 ? '' : 's'}
                  </span>
                  {completed > 0 ? (
                    <span className="payroll-month__done">
                      <i className="bi bi-lock-fill" aria-hidden="true" />
                      {completed} completed
                    </span>
                  ) : null}
                </button>

                <div
                  id={`payroll-month-${month.key}`}
                  className="payroll-month__body"
                  data-open={closed ? 'false' : 'true'}
                >
                  <div className="payroll-month__bodyinner" inert={closed ? '' : undefined}>
                    <div className="payroll-runs">
                      {month.runs.map((run) => (
            <div
              key={run.id}
              className={`payroll-run${run.completed_at ? ' payroll-run--completed' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => openRun(run)}
              onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); openRun(run); } }}
            >
              <div className="payroll-run__head">
                <div className="payroll-run__title">
                  <span className="payroll-run__period">{formatRange(run.period_start, run.period_end)}</span>
                  {run.name ? <span className="payroll-run__name">{run.name}</span> : null}
                  {run.completed_at ? (
                    <span className="payroll-run__tag">
                      <i className="bi bi-lock-fill" aria-hidden="true" />
                      Completed
                    </span>
                  ) : null}
                  {/* Said either way rather than only when it is missing. The
                      question a person brings to this list is "has the journal
                      entry been done", and a card with no tag would leave that
                      unanswered - it would read as a run nobody had looked at
                      rather than as one still owing an entry. */}
                  <span
                    className={`payroll-run__tag payroll-run__tag--journal${run.journal_entry_created ? ' payroll-run__tag--journal-done' : ''}`}
                  >
                    <i
                      className={`bi ${run.journal_entry_created ? 'bi-journal-check' : 'bi-journal-x'}`}
                      aria-hidden="true"
                    />
                    {run.journal_entry_created ? 'Journal entry created' : 'No journal entry'}
                  </span>
                </div>
                <button
                  type="button"
                  className="payroll-run__delete"
                  title="Delete this run"
                  aria-label={`Delete the run for ${formatRange(run.period_start, run.period_end)}`}
                  onClick={(event) => { event.stopPropagation(); setRunToDelete(run); }}
                >
                  <i className="bi bi-trash" aria-hidden="true" />
                </button>
              </div>

              <div className="payroll-run__stats">
                <div>
                  <div className="payroll-run__stat bdc-num">{fmtHours(run.total_hours)}</div>
                  <div className="payroll-run__statLabel">hours</div>
                </div>
                <div>
                  <div className="payroll-run__stat bdc-num">{fmtMoney(run.total_compensation)}</div>
                  <div className="payroll-run__statLabel">compensation</div>
                </div>
                <div>
                  <div className="payroll-run__stat bdc-num">{run.employee_count ?? '\u2014'}</div>
                  <div className="payroll-run__statLabel">employees</div>
                </div>
              </div>

              <div className="payroll-run__foot">
                {/* Once a run is closed, when it was saved stops being the
                    useful fact about it - when it pays is. */}
                <span>
                  {run.completed_at && run.payment_date ? (
                    <>
                      Paid {new Date(`${run.payment_date}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      {run.completed_by_name ? ` · completed by ${run.completed_by_name}` : ''}
                    </>
                  ) : (
                    <>
                      Saved {new Date(run.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      {run.created_by_name ? ` by ${run.created_by_name}` : ''}
                    </>
                  )}
                </span>
                <span className="payroll-run__open">Open <i className="bi bi-arrow-right" aria-hidden="true" /></span>
              </div>
            </div>
                      ))}
                    </div>
                  </div>
                </div>
              </section>
            );
          })}
        </div>
      )}
    </>
  );

  // ------------------------------------------------------------------- run
  const runView = (
    <>
      <PageHeader
        crumbs={[
          { label: 'Home', to: '/' },
          { label: 'Admin', to: '/admin' },
          { label: 'Payroll', to: '/admin/payroll' },
          { label: result ? formatRange(result.period.start, result.period.end) : 'New run' },
        ]}
        icon="bi-cash-stack"
        title={isSaved ? (savedRun.name || 'Saved payroll run') : 'New payroll run'}
        note={isSaved && result ? formatRange(result.period.start, result.period.end) : null}
        backLabel="All runs"
        onBack={backToHome}
        actions={(
          <>
            {isSaved ? (
              // A completed run keeps neither button: its information is part of
              // the record now, and there is nothing left to complete.
              isCompleted ? (
                <span className="payroll-completed-tag">
                  <i className="bi bi-lock-fill" aria-hidden="true" />
                  Completed
                </span>
              ) : (
                <>
                  {/* Only on an open run. A completed one is the record of
                      what was paid, and the branch above keeps neither of
                      these buttons. */}
                  <button
                    type="button"
                    className="bdc-btn"
                    onClick={() => setRepullOpen(true)}
                    disabled={repull.isPending}
                  >
                    <i className="bi bi-arrow-repeat" aria-hidden="true" />
                    Re-pull hours
                  </button>
                  <button type="button" className="bdc-btn" onClick={() => setInfoOpen(true)}>
                    <i className="bi bi-pencil" aria-hidden="true" />
                    Edit information
                  </button>
                  <Button appearance="primary" onClick={() => setCompleteOpen(true)}>
                    <i className="bi bi-check2-circle me-2" aria-hidden="true" />
                    Review and complete
                  </Button>
                </>
              )
            ) : (
              <>
                {/* Only reachable before the pull: afterwards the run is
                    filed and the header shows Edit information instead. */}
                {/* A period can only hold one run, so when the selected one is
                    taken there is nothing to pull - but there is somewhere to
                    go. The button becomes that, rather than sitting disabled
                    and leaving the run to be found on the list. */}
                {result ? null : selectedPeriodRun ? (
                  <Button
                    appearance="primary"
                    onClick={() => openRun(selectedPeriodRun)}
                    loading={openingRun}
                  >
                    <i className="bi bi-box-arrow-in-right me-2" aria-hidden="true" />
                    Open existing run
                  </Button>
                ) : (
                  <Button
                    appearance="primary"
                    onClick={runPull}
                    loading={timesheet.isPending || saveRun.isPending}
                    disabled={employeesQuery.isLoading}
                  >
                    <i className="bi bi-arrow-repeat me-2" aria-hidden="true" />
                    Pull time
                  </Button>
                )}
              </>
            )}
          </>
        )}
      />

      {isCompleted ? (
        <Message type="success" showIcon header="This run is completed">
          Marked completed
          {savedRun.completed_at
            ? ` on ${new Date(savedRun.completed_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
            : ''}
          {savedRun.payment_date
            ? `, for payment on ${new Date(`${savedRun.payment_date}T00:00:00`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
            : ''}
          . These figures are the record of what was paid, so corrections,
          loan repayments and reimbursement syncing are closed.
        </Message>
      ) : null}

      {/* Completed runs only. The journal entry is keyed in from the figures a
          run was signed off on, so before that there is nothing to have created
          and the bar would only be asking a question that cannot be answered
          yet. The review dialog still carries the box, for the case where it
          was done ahead of the sign-off.

          Never disabled: this is a note about work done in QuickBooks, not one
          of the figures completing the run freezes. */}
      {isCompleted ? (
        <div className="payroll-journal">
          <Checkbox
            checked={journalEntryCreated}
            onChange={(_value, checked) => handleJournalEntryChange(checked)}
          >
            <span className="payroll-journal__label">Journal entry created in QuickBooks</span>
          </Checkbox>
          <span className={`payroll-journal__state${journalEntryCreated ? ' payroll-journal__state--done' : ''}`}>
            <i
              className={`bi ${journalEntryCreated ? 'bi-check-circle-fill' : 'bi-circle'}`}
              aria-hidden="true"
            />
            {journalEntryCreated ? 'Created' : 'Not created'}
          </span>
        </div>
      ) : null}

      {/* The SharePoint upload, for the same reason and in the same place as
          the journal entry above it. The review dialog carries this box too,
          but that dialog is only reachable before completion - and the upload
          almost always happens after, which is exactly why it was never made a
          condition of signing off. Without this row the box said it could be
          ticked later and then offered nowhere to tick it.

          Never disabled: a completed run freezes its figures, and this is not
          one of them. */}
      {isCompleted ? (
        <div className="payroll-journal">
          <Checkbox
            checked={sharePointConfirmed}
            onChange={(_value, checked) => handleSharePoint(checked)}
          >
            <span className="payroll-journal__label">Export uploaded to SharePoint</span>
          </Checkbox>
          <span className="payroll-journal__aside">
            {sharePointUrl ? (
              <a href={sharePointUrl} target="_blank" rel="noopener noreferrer">
                Open SharePoint <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
              </a>
            ) : null}
            <span className={`payroll-journal__state${sharePointConfirmed ? ' payroll-journal__state--done' : ''}`}>
              <i
                className={`bi ${sharePointConfirmed ? 'bi-check-circle-fill' : 'bi-circle'}`}
                aria-hidden="true"
              />
              {sharePointConfirmed ? 'Uploaded' : 'Not uploaded'}
            </span>
          </span>
        </div>
      ) : null}

      {/* Says which run is in the way and how it stands; the header button is
          the way to it. The notice below covers the other route to the same
          place - a pull that came back and could not be filed. */}
      {!isSaved && !result && selectedPeriodRun ? (
        <Message type="info" showIcon header="This period has already been run">
          {selectedPeriodRun.name || 'A run'} exists for this pay period
          {selectedPeriodRun.completed_at ? ', and it has been completed' : ''}
          . A period can only hold one run, so use <strong>Open existing run</strong> above
          to see or change it - or pick a different period.
        </Message>
      ) : null}

      {!isSaved && result?.savedRunId ? (
        <Message type="warning" showIcon header="This period is already saved">
          A saved run exists for this pay period, so this one cannot be saved over it. Open it from the run list, or
          delete it there and run the period again.
        </Message>
      ) : null}

      {/* The filters are the run's terms, and a run has them only until it is
          pulled. Changing the period or the roster underneath a set of figures
          would leave the toolbar describing something the sheet is not - so
          once there are figures the filters go, and a different period means a
          new run. */}
      {isSaved || result ? null : (
      <FilterToolbar label="Pay run">
        <FilterField label="Pay Period" basis={260} max={340}>
          <PayPeriodSelect
            data={payPeriodOptions}
            value={period}
            onChange={(next) => setPeriod(next || defaultPeriodValue)}
            flaggedValues={runPeriodValues}
            flagLabel="Already run"
            block
          />
        </FilterField>

        <FilterField label="Employees" basis={320} max={520}>
          <CheckPicker
            data={employeeOptions}
            countable={false}
            renderValue={renderEmployeeSelection(employeeOptions)}
            value={selectedEmployees}
            onChange={(next) => setSelectedEmployees(next || [])}
            placeholder={employeesQuery.isLoading ? 'Loading employees...' : 'Select employees'}
            searchable
            preventOverflow
            renderExtraFooter={renderSelectAllFooter(employeeOptions, selectedEmployees, setSelectedEmployees)}
            block
          />
        </FilterField>
      </FilterToolbar>
      )}

      {employeesQuery.isError ? (
        <Message type="error" showIcon header="Could not load the employee list">
          {employeesQuery.error?.message || 'Try again in a moment.'}
        </Message>
      ) : null}

      {timesheet.isError ? (
        <Message type="error" showIcon header="The payroll pull failed">
          {timesheet.error?.message || 'Try again in a moment.'}
        </Message>
      ) : null}

      {timesheet.isPending ? (
        <div className="payroll-loading">
          <Loader size="md" content={`Pulling TimeCamp for ${range ? formatRange(range.start, range.end) : 'the selected period'}...`} vertical />
        </div>
      ) : null}

      {result && !timesheet.isPending ? (
        <>
          <section className="bdc-metrics payroll-metrics">
            {result.categories.map((category) => (
              <div className="bdc-metric" key={category.key}>
                <div className="bdc-metric__body">
                  <div>
                    <p className="bdc-metric__label">{category.label}</p>
                    <p className="bdc-metric__value bdc-num">{fmtHours(totals[category.key])}</p>
                  </div>
                  <div
                    className="bdc-metric__foot"
                    style={{ color: CATEGORY_ACCENT[category.key] }}
                  >
                    {totals.total > 0
                      ? `${Math.round((totals[category.key] / totals.total) * 100)}% of hours`
                      : 'No hours'}
                  </div>
                </div>
              </div>
            ))}
            <div className="bdc-metric">
              <div className="bdc-metric__body">
                <div>
                  <p className="bdc-metric__label">Total Hours</p>
                  <p className="bdc-metric__value bdc-num">{fmtHours(totals.total)}</p>
                </div>
                <div className="bdc-metric__foot">
                  {result.entryCount} entries &middot; {result.employees.length} employees
                </div>
              </div>
            </div>
          </section>

          {/* Corrections stay editable on a saved run - they are the figures
              a person enters by hand, and fixing one after a run is filed is
              the point. The settings button does go: the match rate is a rule,
              and a saved run is frozen against those. */}
          {/* On a completed run the save handlers are omitted rather than
              disabled: the matrix already renders a plain figure when it has no
              handler, which is exactly what a closed run's correction is. */}
          <PayrollMatrix
            result={result}
            onEditSettings={isSaved ? undefined : () => setSettingsOpen(true)}
            onSaveCorrection={isCompleted ? undefined : persistCorrection}
            onSaveAdjustment={isCompleted ? undefined : persistAdjustment}
            onSaveReimbursementAdjustment={isSaved && !isCompleted ? persistReimbursementAdjustment : undefined}
            onSaveReimbursementAdjustmentBulk={isSaved && !isCompleted ? persistReimbursementAdjustmentBulk : undefined}
            onExport={exportExcel}
            onExportQuickBooks={exportQuickBooks}
            reimbursementDrift={isCompleted ? null : driftQuery.data}
            onOpenReimbursementDrift={() => setDriftOpen(true)}
            onSyncReimbursements={isSaved && !isCompleted ? syncReimbursements : undefined}
            syncingReimbursements={refreshReimbursements.isPending}
          />

          {result.unknownTasks.length > 0 ? (
            <Message type="warning" showIcon header="Some tasks were not in the TimeCamp catalog">
              {result.unknownTasks.length} task{result.unknownTasks.length === 1 ? ' was' : 's were'} not returned by
              the tasks API (usually archived since the time was logged), covering{' '}
              {fmtHours(result.unknownTasks.reduce((sum, task) => sum + task.hours, 0))} hours. Those entries are
              counted, categorised from the flag TimeCamp stored on the entry itself, and flagged in the tables below.
            </Message>
          ) : null}

          <div className="payroll-employees">
            {result.employees.map((employee) => (
              <EmployeeBreakdown key={employee.employeeId} employee={employee} />
            ))}
          </div>
        </>
      ) : null}

      <PayrollCompleteRunModal
        open={completeOpen}
        result={result}
        period={result ? formatRange(result.period.start, result.period.end) : null}
        drift={driftQuery.data}
        journalEntryCreated={journalEntryCreated}
        timesheetDrift={timesheetDrift.data || null}
        timesheetDriftLoading={timesheetDrift.isFetching}
        timesheetDriftFailed={timesheetDrift.isError}
        onRepull={repullRun}
        repulling={repull.isPending}
        exports={runExports.data?.exports || []}
        hasCurrentExport={Boolean(runExports.data?.hasCurrentExport)}
        figuresUpdatedAt={runExports.data?.figuresUpdatedAt || null}
        exportsLoading={runExports.isLoading}
        onExport={exportExcel}
        exporting={recordExport.isPending}
        sharePointUrl={sharePointUrl}
        sharePointConfirmed={sharePointConfirmed}
        onSharePoint={handleSharePoint}
        onClose={() => setCompleteOpen(false)}
        onComplete={handleCompleteRun}
        completing={completeRun.isPending}
      />

      <PayrollRepullModal
        open={repullOpen}
        result={result}
        period={result ? formatRange(result.period.start, result.period.end) : null}
        onClose={() => setRepullOpen(false)}
        onConfirm={repullRun}
        repulling={repull.isPending}
      />

      <PayrollReimbursementDriftModal
        open={driftOpen}
        drift={driftQuery.data}
        onClose={() => setDriftOpen(false)}
        onSync={syncReimbursements}
        syncing={refreshReimbursements.isPending}
      />

      <PayrollSettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        employerMatchPercent={result?.employerMatchPercent ?? 6}
        onSave={saveEmployerMatch}
        saving={saveMatch.isPending}
      />

      {!result && !timesheet.isPending ? (
        <div className="payroll-intro">
          <i className="bi bi-cash-stack" aria-hidden="true" />
          <h2>Pull a pay period</h2>
          <p>
            Choose a pay period and the employees to include, then pull their TimeCamp time. It comes back split into
            Direct, Indirect and Fringe Benefits, with every task each person worked on. Save it when it looks right
            and the figures are frozen as they stand.
          </p>
        </div>
      ) : null}
    </>
  );

  return (
    // The sheet is worth every pixel a wide monitor has - it gains an employee
    // column per ~150px. The run list is a card grid and stays on the app's
    // normal measure, where it would just stretch.
    <div className={`bdc-page${view === 'run' ? ' payroll-page--wide' : ''}`}>
      {view === 'home' ? homeView : runView}

      <PayrollRunInfoModal
        open={infoOpen}
        run={savedRun}
        periodLabel={result ? formatRange(result.period.start, result.period.end) : ''}
        onClose={() => setInfoOpen(false)}
        onSave={saveRunInfo}
        onDelete={deleteOpenRun}
        saving={updateRun.isPending}
        deleting={deleteRun.isPending}
      />

      <ConfirmModal
        open={!!runToDelete}
        title="Delete payroll run"
        body={(
          <>
            Delete the saved run for{' '}
            <strong>{runToDelete ? formatRange(runToDelete.period_start, runToDelete.period_end) : 'this period'}</strong>?
            This cannot be undone. The corrections and loan repayments entered on it are deleted with it, so
            pulling the period again gives a genuinely fresh run.
          </>
        )}
        confirmText="Delete"
        danger
        loading={deleteRun.isPending}
        onConfirm={confirmDeleteRun}
        onCancel={() => setRunToDelete(null)}
      />
    </div>
  );
}
